import { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import { supabase } from './supabase';
import Login from './Login';
import DashboardOverview from './DashboardOverview';
import ErrorBoundary from './ErrorBoundary';
import { Wordmark, VineMark, Mono, Btn } from './components/ui';
import ThemeSwitcher from './ThemeSwitcher';

// Every page except the landing overview is code-split: a client's first load
// only downloads the shell + overview, and each tab's chunk loads the first
// time it's opened. The admin-only views (AdminPortal is ~2k lines) never
// reach client browsers at all.
//
// A chunk can fail to load when a deploy replaced the build while this tab was
// open (the old chunk files no longer exist). Reload once to pick up the new
// build instead of erroring; the session flag stops a reload loop when the
// chunk is genuinely unreachable (offline, CDN down).
const CHUNK_RELOAD_FLAG = 'graphvine-chunk-reload';
function lazyPage(importer) {
  return lazy(() => importer().then(
    (mod) => {
      try { sessionStorage.removeItem(CHUNK_RELOAD_FLAG); } catch { /* storage blocked */ }
      return mod;
    },
    (err) => {
      let reloaded = true;
      try { reloaded = !!sessionStorage.getItem(CHUNK_RELOAD_FLAG); } catch { /* storage blocked */ }
      if (reloaded) throw err;
      try { sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1'); } catch { /* storage blocked */ }
      window.location.reload();
      return new Promise(() => {}); // the page is reloading
    },
  ));
}

const CompetitorMap       = lazyPage(() => import('./CompetitorMap'));
const Admin               = lazyPage(() => import('./Admin'));
const AdminPortal         = lazyPage(() => import('./AdminPortal'));
const Brands              = lazyPage(() => import('./Brands'));
const BrandCoverage       = lazyPage(() => import('./BrandCoverage'));
const PriceChangeAlerts   = lazyPage(() => import('./PriceChangeAlerts'));
const CompetitorCatalog   = lazyPage(() => import('./CompetitorCatalog'));
const BestSellers         = lazyPage(() => import('./BestSellers'));
const NewProductDetection = lazyPage(() => import('./NewProductDetection'));
const SaleDiscountTracker = lazyPage(() => import('./SaleDiscountTracker'));
const SupportChat         = lazyPage(() => import('./SupportChat'));

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || 'kevinrealtordang@gmail.com';

const NAV = [
  { label: 'Dashboard',          icon: 'dashboard'      },
  { label: 'Map',                icon: 'map'            },
  { label: 'Brands',             icon: 'style'          },
  { label: 'Competitor Catalog', icon: 'storefront'     },
  { label: 'Best Sellers',       icon: 'trending_up'    },
  { label: 'Price Activity',     icon: 'price_change'   },
  { label: 'New Products',       icon: 'new_releases'   },
  { label: 'Support',            icon: 'support_agent'  },
];

const SUB_NAVS = {
  'Brands': [
    { label: 'Overview', icon: 'style'     },
    { label: 'Coverage', icon: 'analytics' },
  ],
  'Price Activity': [
    { label: 'Price Alerts',      icon: 'price_change' },
    { label: 'Sales & Discounts', icon: 'local_offer'  },
  ],
};

const DEFAULT_SUB = {
  'Brands':         'Overview',
  'Price Activity': 'Price Alerts',
};

function distanceMiles(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function PasswordResetScreen({ onDone, isInvite = false }) {
  const [password, setPassword]   = useState('');
  const [confirm, setConfirm]     = useState('');
  const [loading, setLoading]     = useState(false);
  const [msg, setMsg]             = useState(null);
  const [showPw, setShowPw]       = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = async () => {
    if (password.length < 6) return setMsg({ text: 'Password must be at least 6 characters.', type: 'error' });
    if (password !== confirm)  return setMsg({ text: 'Passwords do not match.', type: 'error' });
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) return setMsg({ text: error.message, type: 'error' });
    setMsg({ text: 'Password set! Redirecting…', type: 'success' });
    setTimeout(onDone, 1500);
  };

  const inputBoxStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 14px',
    borderRadius: 6,
    background: 'var(--g-card-flat)',
    border: '1px solid var(--g-border-hi)',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--g-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <div style={{ width: '100%', maxWidth: 400 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 32 }}>
          <Wordmark size={20} />
          <Mono size={12} color="var(--g-text-muted)" style={{ marginTop: 10, letterSpacing: '0.01em', }}>
            {isInvite ? 'Welcome — Set your password' : 'Set new password'}
          </Mono>
        </div>
        <div
          style={{
            padding: 24,
            background: 'var(--g-card)',
            border: '1px solid var(--g-border)',
            borderRadius: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--g-text)' }}>
            {isInvite ? 'Create your password' : 'Choose a new password'}
          </h2>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--g-text-dim)' }}>
            {isInvite
              ? "You've been invited to Graphvine. Set a password to activate your account."
              : 'Enter and confirm your new password below.'}
          </p>
          {[
            { label: 'New password',     val: password, set: setPassword, show: showPw,     toggle: () => setShowPw(v => !v) },
            { label: 'Confirm password', val: confirm,  set: setConfirm,  show: showConfirm, toggle: () => setShowConfirm(v => !v) },
          ].map(f => (
            <div key={f.label}>
              <label
                style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--g-text-dim)',
                  marginBottom: 6,
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                {f.label}
              </label>
              <div style={inputBoxStyle}>
                <input
                  type={f.show ? 'text' : 'password'}
                  value={f.val}
                  onChange={e => f.set(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                  placeholder="••••••••"
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: 'var(--g-text)',
                    fontSize: 13,
                    fontFamily: 'Inter, sans-serif',
                    caretColor: 'var(--g-accent)',
                  }}
                />
                <button
                  type="button"
                  onClick={f.toggle}
                  style={{ background: 'transparent', border: 'none', color: 'var(--g-text-faint)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                  tabIndex={-1}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{f.show ? 'visibility_off' : 'visibility'}</span>
                </button>
              </div>
            </div>
          ))}
          {msg && (
            <p
              style={{
                margin: 0,
                                fontSize: 12,
                padding: '8px 12px',
                borderRadius: 6,
                background: msg.type === 'success' ? 'rgba(16,185,129,0.10)' : 'rgba(239,68,68,0.10)',
                border: msg.type === 'success' ? '1px solid rgba(16,185,129,0.30)' : '1px solid rgba(239,68,68,0.30)',
                color: msg.type === 'success' ? 'var(--g-accent-hi)' : 'var(--g-danger)',
              }}
            >
              {msg.text}
            </p>
          )}
          <Btn
            variant="primary"
            size="lg"
            onClick={handleSubmit}
            disabled={loading}
            style={{ justifyContent: 'center', width: '100%' }}
          >
            {loading ? 'Saving…' : isInvite ? 'Activate account →' : 'Set password →'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function LoadingSplash({ message = 'Loading…' }) {
  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--g-bg)',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16, opacity: 0.85 }}>
          <VineMark size={40} color="var(--g-accent)" />
        </div>
        <Mono size={12} color="var(--g-text-dim)" style={{ letterSpacing: '0.01em', }}>
          {message}
        </Mono>
      </div>
    </div>
  );
}

// Shown in the content pane while a tab's code chunk downloads (first open only).
function TabLoading() {
  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 240, alignItems: 'center', justifyContent: 'center' }}>
      <Mono size={12} color="var(--g-text-dim)">Loading…</Mono>
    </div>
  );
}

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myStore, setMyStore] = useState(null);
  const [myStores, setMyStores] = useState([]);
  const [activeNav, setActiveNav] = useState('Dashboard');
  const [subNav, setSubNav]       = useState('');
  const [supportUnread, setSupportUnread] = useState(0);
  const [supportToast, setSupportToast] = useState(false);
  const [passwordRecovery, setPasswordRecovery] = useState(() => {
    try {
      const hash = new URLSearchParams(window.location.hash.replace('#', ''));
      return hash.get('type') === 'recovery';
    } catch { return false; }
  });
  const [inviteFlow, setInviteFlow] = useState(() => {
    try {
      const hash = new URLSearchParams(window.location.hash.replace('#', ''));
      return hash.get('type') === 'invite';
    } catch { return false; }
  });
  const [radius, setRadius] = useState(() => {
    try {
      const r = localStorage.getItem('gs_radius');
      return r ? parseInt(r, 10) : 5;
    } catch { return 5; }
  });

  const setRadiusPersist = useCallback((val) => {
    setRadius(val);
    try { val ? localStorage.setItem('gs_radius', val) : localStorage.removeItem('gs_radius'); } catch {}
  }, []);

  const [radiusOpen, setRadiusOpen] = useState(false);
  const radiusRef = useRef(null);
  useEffect(() => {
    if (!radiusOpen) return;
    const handler = e => { if (radiusRef.current && !radiusRef.current.contains(e.target)) setRadiusOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [radiusOpen]);

  const navigate = useCallback((label) => {
    const subNavMap = {
      'Price Alerts':      { nav: 'Price Activity', sub: 'Price Alerts'      },
      'Sales & Discounts': { nav: 'Price Activity', sub: 'Sales & Discounts' },
      'Brand Coverage':    { nav: 'Brands',         sub: 'Coverage'          },
    };
    if (subNavMap[label]) {
      setActiveNav(subNavMap[label].nav);
      setSubNav(subNavMap[label].sub);
    } else {
      setActiveNav(label);
      setSubNav(DEFAULT_SUB[label] || '');
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthChecked(true);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true);
      // Keep the existing user object when it's the same user. Supabase fires
      // TOKEN_REFRESHED / SIGNED_IN with a NEW user object every time the tab
      // regains focus; swapping state to that fresh reference re-ran every
      // [user] effect — refetching stores (setLoading flashed the whole
      // dashboard like a page reload) and inserting a spurious login_events
      // session row on each tab switch.
      setUser(prev => (prev?.id === (session?.user?.id ?? null) ? prev : session?.user ?? null));
    });
    return () => subscription.unsubscribe();
  }, []);

  // Session tracking
  const sessionIdRef    = useRef(null);
  const sessionStartRef = useRef(null);

  useEffect(() => {
    if (!user || user.email === ADMIN_EMAIL) return;

    let interval;
    const startSession = async () => {
      let ipData = {};
      try {
        const res = await fetch('https://ipapi.co/json/');
        if (res.ok) {
          const json = await res.json();
          ipData = { ip_address: json.ip, city: json.city, region: json.region, country: json.country_name };
        }
      } catch {}

      let result = await supabase
        .from('login_events')
        .insert({ user_id: user.id, email: user.email, ...ipData })
        .select('id')
        .single();

      if (result.error && Object.keys(ipData).length > 0) {
        console.warn('[session] IP insert failed, retrying without IP:', result.error.message);
        result = await supabase
          .from('login_events')
          .insert({ user_id: user.id, email: user.email })
          .select('id')
          .single();
      }

      if (result.error) {
        console.error('[session] login_events insert failed:', result.error);
      }

      if (result.data) {
        sessionIdRef.current    = result.data.id;
        sessionStartRef.current = Date.now();
      }
      interval = setInterval(async () => {
        if (!sessionIdRef.current) return;
        await supabase.from('login_events')
          .update({ last_seen_at: new Date().toISOString() })
          .eq('id', sessionIdRef.current);
      }, 60 * 1000);
    };

    startSession();
    return () => {
      clearInterval(interval);
      if (sessionIdRef.current) {
        supabase.from('login_events')
          .update({ last_seen_at: new Date().toISOString() })
          .eq('id', sessionIdRef.current);
      }
    };
  }, [user]);

  const fetchStores = useCallback(async (currentUser) => {
    setLoading(true);
    // Independent queries — fetched in parallel; the splash screen blocks the
    // whole portal until both land.
    const [{ data: storeData }, { data: profileStores }] = await Promise.all([
      supabase.from('stores').select('*').order('name'),
      supabase
        .from('profile_stores').select('store_id, is_default')
        .eq('profile_id', currentUser.id),
    ]);
    if (storeData) setStores(storeData);

    if (profileStores && profileStores.length > 0) {
      const ids = profileStores.map(ps => ps.store_id);
      setMyStores(ids);
      setMyStore((profileStores.find(ps => ps.is_default) || profileStores[0]).store_id);
    } else if (storeData?.length > 0) {
      setMyStore(storeData[0].id);
      setMyStores([storeData[0].id]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (authChecked && user) fetchStores(user);
  }, [authChecked, user, fetchStores]);

  const activeNavRef = useRef(activeNav);
  useEffect(() => { activeNavRef.current = activeNav; }, [activeNav]);

  // Nav badge = the user's unread admin replies (read_at IS NULL) — the SAME
  // signal the in-chat per-ticket dots use, so the nav count and the thread
  // list always agree. RLS scopes support_messages to the user's own tickets.
  // (Previously this counted tickets touched since a localStorage timestamp,
  // which double-counted status changes and cleared every ticket's badge as
  // soon as one was opened.)
  const refreshSupportUnread = useCallback(async () => {
    if (!user || user.email === ADMIN_EMAIL) return;
    const { count, error } = await supabase
      .from('support_messages')
      .select('*', { count: 'exact', head: true })
      .eq('is_admin', true)
      .is('read_at', null);
    if (error) { console.warn('[support] unread count failed:', error.message); return; }
    const n = count ?? 0;
    setSupportUnread(prev => {
      if (n > prev && activeNavRef.current !== 'Support') setSupportToast(true);
      return n;
    });
  }, [user]);

  useEffect(() => {
    if (!user || user.email === ADMIN_EMAIL) return;
    refreshSupportUnread();
    const id = setInterval(refreshSupportUnread, 15000);
    return () => clearInterval(id);
  }, [user, refreshSupportUnread]);

  useEffect(() => {
    if (!supportToast) return;
    const t = setTimeout(() => setSupportToast(false), 10000);
    return () => clearTimeout(t);
  }, [supportToast]);

  const myStoreData = useMemo(() => stores.find(s => s.id === myStore), [stores, myStore]);

  const filteredStores = useMemo(() => stores.filter(s => {
    if (myStores.includes(s.id)) return true;
    if (!myStoreData?.lat || !s.lat) return true;
    if (!radius) return true;
    return distanceMiles(myStoreData.lat, myStoreData.lng, s.lat, s.lng) <= radius;
  }), [stores, myStores, radius, myStoreData]);

  const handleLogout = async () => { await supabase.auth.signOut(); setUser(null); };
  const myStoreName = useMemo(() => stores.find(s => s.id === myStore)?.name, [stores, myStore]);

  if (passwordRecovery) return <PasswordResetScreen onDone={() => setPasswordRecovery(false)} />;
  if (inviteFlow)       return <PasswordResetScreen isInvite onDone={() => setInviteFlow(false)} />;
  if (!authChecked)     return <LoadingSplash message="Loading…" />;
  if (!user)            return <Login onLogin={setUser} />;
  if (user.email === ADMIN_EMAIL) return (
    <Suspense fallback={<LoadingSplash message="Loading…" />}>
      <AdminPortal user={user} />
    </Suspense>
  );
  if (loading || !myStore)        return <LoadingSplash message="Initializing Graphvine…" />;

  const userInitials = (user?.email || '').slice(0, 2).toUpperCase();

  return (
    // Desktop-only by design (no mobile breakpoints). minWidth + overflowX
    // make a too-narrow window scroll horizontally with the layout intact,
    // instead of overflow:hidden silently clipping tables off-screen.
    <div style={{ height: '100vh', overflowX: 'auto', overflowY: 'hidden', background: 'var(--g-bg)', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ display: 'flex', height: '100vh', width: '100%', minWidth: 1200, overflow: 'hidden' }}>

        {/* ─── Sidebar ──────────────────────────────────────────────────── */}
        <aside
          style={{
            width: 220,
            flexShrink: 0,
            height: '100%',
            background: 'var(--g-sidebar)',
            borderRight: '1px solid var(--g-border)',
            display: 'flex',
            flexDirection: 'column',
            padding: '20px 14px',
            backdropFilter: 'blur(8px)',
          }}
        >
          {/* Logo */}
          <div
            onClick={() => navigate('Dashboard')}
            title="Go to Dashboard"
            style={{ display: 'flex', alignItems: 'center', marginBottom: 28, paddingLeft: 4, cursor: 'pointer' }}
          >
            <Wordmark size={16} />
          </div>

          {/* Nav */}
          <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {NAV.map(item => {
              const isActive = activeNav === item.label;
              return (
                <button
                  key={item.label}
                  onClick={() => {
                    navigate(item.label);
                    if (item.label === 'Support') {
                      // Don't fake-clear the badge on tab click — it now
                      // reflects real unread and drops as each ticket is opened.
                      setSupportToast(false);
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    borderRadius: 6,
                    background: isActive ? 'var(--g-accent-dim)' : 'transparent',
                    color: isActive ? 'var(--g-accent-hi)' : 'var(--g-text-dim)',
                    borderLeft: isActive ? '2px solid var(--g-accent)' : '2px solid transparent',
                    border: 'none',
                    borderLeftWidth: 2,
                    fontSize: 13,
                    fontWeight: isActive ? 600 : 500,
                    fontFamily: 'Inter, sans-serif',
                    cursor: 'pointer',
                    transition: 'background 0.15s, color 0.15s',
                    textAlign: 'left',
                    width: '100%',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--g-hover)'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 18, color: isActive ? 'var(--g-accent-hi)' : 'var(--g-text-dim)' }}>{item.icon}</span>
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.label === 'Support' && supportUnread > 0 && (
                    <span
                      style={{
                        background: 'var(--g-danger)',
                        color: '#fff',
                        borderRadius: 999,
                                                fontSize: 11,
                        fontWeight: 700,
                        padding: '1px 6px',
                                              }}
                    >
                      {supportUnread}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Footer */}
          <div style={{ marginTop: 'auto', paddingTop: 16, borderTop: '1px solid var(--g-border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                background: 'transparent',
                border: 'none',
                color: 'var(--g-text-dim)',
                fontSize: 12,
                fontFamily: 'Inter, sans-serif',
                cursor: 'pointer',
                borderRadius: 6,
                textAlign: 'left',
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--g-text)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--g-text-dim)'}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 15 }}>logout</span>
              Sign out
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  background: 'var(--g-accent-dim)',
                  border: '1px solid var(--g-accent-border)',
                  color: 'var(--g-accent-hi)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {userInitials}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--g-text)',
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {myStoreName || 'Your store'}
                </div>
                <Mono size={12} color="var(--g-text-faint)" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.email}
                </Mono>
              </div>
            </div>
          </div>
        </aside>

        {/* ─── Main content ─────────────────────────────────────────────── */}
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>

          {/* Topbar */}
          <header
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 28px',
              borderBottom: '1px solid var(--g-border)',
              background: 'var(--g-topbar)',
              backdropFilter: 'blur(8px)',
              flexShrink: 0,
              position: 'sticky',
              top: 0,
              zIndex: 20,
            }}
          >
            <div>
              <span style={{ fontSize: 12, color: 'var(--g-text-faint)', display: 'block', marginBottom: 4 }}>
                {subNav ? `${activeNav} · ${subNav}` : 'Daily overview'}
              </span>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--g-text)', letterSpacing: '-0.01em' }}>
                  {activeNav}
                </h1>
                {subNav && <span style={{ fontSize: 13, color: 'var(--g-text-dim)' }}>{subNav}</span>}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* Theme toggle */}
              <ThemeSwitcher />

              {/* Store chip */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid var(--g-border-hi)',
                  background: 'var(--g-input)',
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 14, color: 'var(--g-accent-hi)' }}>store</span>
                <Mono size={12} color="var(--g-text-dim)" style={{ textTransform: 'none' }}>{myStoreName}</Mono>
              </div>

              {/* Radius selector */}
              <div ref={radiusRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setRadiusOpen(o => !o)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: `1px solid ${radiusOpen ? 'var(--g-accent-border)' : 'var(--g-border-hi)'}`,
                    background: radiusOpen ? 'var(--g-accent-dim)' : 'var(--g-input)',
                    cursor: 'pointer',
                                        fontSize: 12,
                    color: radiusOpen ? 'var(--g-accent-hi)' : 'var(--g-text-dim)',
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 14, color: radiusOpen ? 'var(--g-accent-hi)' : 'var(--g-text-dim)' }}>radar</span>
                  <span>{radius} mi</span>
                  <span style={{ fontSize: 11 }}>{radiusOpen ? '▲' : '▼'}</span>
                </button>
                {radiusOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 6px)',
                      left: 0,
                      zIndex: 100,
                      border: '1px solid var(--g-border-hi)',
                      background: 'var(--g-overlay)',
                      minWidth: '100%',
                      borderRadius: 6,
                      overflow: 'hidden',
                      boxShadow: 'var(--g-shadow)',
                    }}
                  >
                    <div style={{ padding: '7px 14px 5px', fontSize: 11, color: 'var(--g-text-faint)', borderBottom: '1px solid var(--g-border-faint)' }}>
                      Scopes competitor data on all pages
                    </div>
                    {[5, 10, 15].map(r => (
                      <button
                        key={r}
                        onClick={() => { setRadiusPersist(r); setRadiusOpen(false); }}
                        style={{
                          width: '100%',
                          padding: '8px 14px',
                          textAlign: 'left',
                          background: radius === r ? 'var(--g-accent-dim)' : 'transparent',
                          color: radius === r ? 'var(--g-accent-hi)' : 'var(--g-text-dim)',
                                                    fontSize: 12,
                          fontWeight: radius === r ? 600 : 500,
                          border: 'none',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={e => { if (radius !== r) e.currentTarget.style.background = 'var(--g-hover)'; }}
                        onMouseLeave={e => { if (radius !== r) e.currentTarget.style.background = 'transparent'; }}
                      >
                        {r} mi
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Sign out */}
              <Btn variant="ghost" size="sm" onClick={handleLogout} icon="logout">
                Sign out
              </Btn>
            </div>
          </header>

          <ErrorBoundary key={activeNav}>
            {/* Sub-tab bar */}
            {SUB_NAVS[activeNav] && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '0 28px',
                  borderBottom: '1px solid var(--g-border)',
                  background: 'var(--g-subbar)',
                  backdropFilter: 'blur(8px)',
                  position: 'sticky',
                  top: 65,
                  zIndex: 10,
                  flexShrink: 0,
                }}
              >
                {SUB_NAVS[activeNav].map(sub => {
                  const isActive = (subNav || DEFAULT_SUB[activeNav]) === sub.label;
                  return (
                    <button
                      key={sub.label}
                      onClick={() => setSubNav(sub.label)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '10px 14px',
                        fontSize: 12,
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? 'var(--g-accent-hi)' : 'var(--g-text-dim)',
                        borderBottom: isActive ? '2px solid var(--g-accent)' : '2px solid transparent',
                        marginBottom: -1,
                        background: 'transparent',
                        cursor: 'pointer',
                        fontFamily: 'Inter, sans-serif',
                        border: 'none',
                        borderBottomWidth: 2,
                      }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{sub.icon}</span>
                      {sub.label}
                    </button>
                  );
                })}
              </div>
            )}

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {/* Page routing */}
              <Suspense fallback={<TabLoading />}>
              {activeNav === 'Map' ? (
                <CompetitorMap myStoreId={myStore} myStoreIds={myStores} stores={stores} radius={radius} setRadius={setRadiusPersist} />
              ) : activeNav === 'Brands' && (subNav || DEFAULT_SUB['Brands']) === 'Coverage' ? (
                <BrandCoverage myStoreId={myStore} myStores={myStores} stores={filteredStores} />
              ) : activeNav === 'Brands' ? (
                <Brands myStoreId={myStore} myStores={myStores} stores={filteredStores} />
              ) : activeNav === 'Competitor Catalog' ? (
                <CompetitorCatalog myStoreId={myStore} myStores={myStores} stores={filteredStores} />
              ) : activeNav === 'Best Sellers' ? (
                <BestSellers myStoreId={myStore} myStores={myStores} stores={filteredStores} />
              ) : activeNav === 'Price Activity' && (subNav || DEFAULT_SUB['Price Activity']) === 'Sales & Discounts' ? (
                <SaleDiscountTracker myStoreId={myStore} myStores={myStores} stores={filteredStores} />
              ) : activeNav === 'Price Activity' ? (
                <PriceChangeAlerts myStoreId={myStore} myStores={myStores} stores={filteredStores} />
              ) : activeNav === 'New Products' ? (
                <NewProductDetection myStoreId={myStore} myStores={myStores} stores={filteredStores} />
              ) : activeNav === 'Support' ? (
                <SupportChat user={user} onRead={refreshSupportUnread} />
              ) : activeNav === 'Admin' ? (
                <Admin user={user} onBack={() => navigate('Dashboard')} />
              ) : (
                <DashboardOverview
                  myStoreId={myStore}
                  myStores={myStores}
                  stores={filteredStores}
                  myStoreName={myStoreName}
                  onNavigate={navigate}
                />
              )}
              </Suspense>
            </div>
          </ErrorBoundary>
        </main>

        {/* Support reply toast */}
        {supportToast && (
          <div
            style={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 50,
              width: 320,
              background: 'var(--g-overlay)',
              border: '1px solid var(--g-accent-border)',
              borderRadius: 8,
              padding: '14px 18px',
              boxShadow: 'var(--g-shadow)',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 16, color: 'var(--g-accent-hi)' }}>support_agent</span>
                <Mono size={12} color="var(--g-accent-hi)" style={{ letterSpacing: '0.01em', fontWeight: 600 }}>
                  Support reply
                </Mono>
              </div>
              <button
                onClick={() => setSupportToast(false)}
                style={{ background: 'transparent', border: 'none', color: 'var(--g-text-muted)', cursor: 'pointer', display: 'flex' }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
              </button>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--g-text-dim)', paddingLeft: 24 }}>
              The Graphvine team replied to your support issue.
            </p>
            <button
              onClick={() => { navigate('Support'); setSupportUnread(0); setSupportToast(false); }}
              style={{
                marginLeft: 24,
                marginTop: 4,
                width: 'fit-content',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                                fontSize: 12,
                color: 'var(--g-accent-hi)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              View reply
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>arrow_forward</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
